import { ModalImpression } from "@components/ModalImpression/ModalImpression";
import { useUserInfo } from "@hooks/useUserInfo/useUserInfo";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Button, ButtonSizes } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Modal } from "@jmc/solid-design-system/src/components/molecules/Modal/Modal";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { CMSLoginDisclaimer } from "@jwm/types/CMSLoginDisclaimer";
import ResizeObserver from "@zeecoder/react-resize-observer";
import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useState } from "react";

import { login, register } from "../../api/auth";
import style from "./style.module.scss";

const JWMLoginDisclaimerBreakpoint = 767;
interface Props {
    onConfirm?: () => void;
    onCancel?: () => void;
    onClose?: () => void;
    onRegister?: () => void;
}

export const JWMLoginDisclaimer: React.FunctionComponent<Props> = ({
    onConfirm = () => null,
    onCancel = () => null,
    onClose = null,
    onRegister = () => null,
}: Props): JSX.Element => {
    const { allContentstackDisclaimers, allContentstackMainNavigation } = useStaticQuery(graphql`
        query {
            allContentstackDisclaimers {
                nodes {
                    login_disclaimer {
                        title
                        text
                        registration_text
                        registration_link_text
                        login_button_text
                        cancel_button_text
                        disclaimer_text
                    }
                    publish_details {
                        locale
                    }
                }
            }
            allContentstackMainNavigation {
                nodes {
                    show_login_controls
                    publish_details {
                        locale
                    }
                }
            }
        }
    `);

    const locale = useLocale();
    const userInfo = useUserInfo();
    const [open, setOpen] = useState(false);
    const [buttonSize, setButtonSize] = useState<ButtonSizes>("medium");
    const { jnjFullBranded } = useJnjBranding();
    const isMobile = useMediaQuery(BreakPoint.md);

    const localizedDisclaimer = allContentstackDisclaimers?.nodes
        .filter((disclaimer: CMSLoginDisclaimer) => disclaimer.publish_details.locale === locale)
        .pop();

    const showLoginControls = allContentstackMainNavigation?.nodes
        .filter(
            (disclaimer: { show_login_controls: boolean; publish_details: { locale: string } }) =>
                disclaimer.publish_details.locale === locale,
        )
        .pop()?.show_login_controls;

    useEffect(() => {
        setOpen(!userInfo.isAuthenticated());
    }, [userInfo.isAuthenticated()]);

    useEffect(() => {
        if (jnjFullBranded && isMobile) {
            setButtonSize("small");
        }
    }, [jnjFullBranded, isMobile]);

    return showLoginControls ? (
        <ResizeObserver>
            {(ref: React.RefObject<HTMLDivElement>, width: number) => {
                return (
                    <div ref={ref} data-test-id="login-disclaimer">
                        {open && (
                            <ModalImpression open={open} type={"Login disclaimer"} trackOnlyOnce={true}>
                                <Modal
                                    size="medium"
                                    open={true}
                                    position={jnjFullBranded ? "center" : "topRight"}
                                    simpleCloseIcon={jnjFullBranded}
                                    spacing={jnjFullBranded ? "xxl" : "large"}
                                    onClose={() => {
                                        setOpen(false);
                                        onClose && onClose();
                                    }}
                                >
                                    {jnjFullBranded && (
                                        <Modal.Title title={localizedDisclaimer?.login_disclaimer?.title} />
                                    )}
                                    <div data-test-id="login-disclaimer-content" className={style.brandingSpacing}>
                                        {!jnjFullBranded && (
                                            <div key="modal-title" className={style.modalTitle}>
                                                <Modal.Title title={localizedDisclaimer?.login_disclaimer?.title} />
                                            </div>
                                        )}
                                        <div key="modal-content" className={style.modalMainContent}>
                                            <Modal.Content>
                                                {localizedDisclaimer?.login_disclaimer?.text && (
                                                    <Typography color="gray" key="modal-text">
                                                        {localizedDisclaimer?.login_disclaimer?.text}
                                                    </Typography>
                                                )}
                                                {jnjFullBranded && <br />}
                                                <div className={style.registrationSection} key="registration-section">
                                                    {localizedDisclaimer?.login_disclaimer?.registration_text && (
                                                        <div className={style.registrationText} key="reg-text">
                                                            <Typography color="gray">
                                                                {
                                                                    localizedDisclaimer?.login_disclaimer
                                                                        ?.registration_text
                                                                }
                                                            </Typography>
                                                        </div>
                                                    )}

                                                    {localizedDisclaimer?.login_disclaimer?.registration_link_text && (
                                                        <div className={style.registrationLink} key="reg-link">
                                                            <Typography
                                                                variant={jnjFullBranded ? "link" : "body"}
                                                                color={jnjFullBranded ? "primary" : "title"}
                                                                data-tracking-action="Register"
                                                                onClick={() => {
                                                                    register();
                                                                    setOpen(false);
                                                                    onRegister && onRegister();
                                                                }}
                                                            >
                                                                {
                                                                    localizedDisclaimer?.login_disclaimer
                                                                        ?.registration_link_text
                                                                }
                                                            </Typography>
                                                        </div>
                                                    )}
                                                </div>
                                            </Modal.Content>
                                        </div>
                                        <Modal.Actions key="modal-actions">
                                            {jnjFullBranded && (
                                                <div key="cancel-button" className={style.cancelButton}>
                                                    <Button
                                                        data-tracking-action="Cancel"
                                                        size={buttonSize as ButtonSizes}
                                                        focusable
                                                        onClick={() => {
                                                            setOpen(false);
                                                            onClose && onClose();
                                                            onCancel && onCancel();
                                                        }}
                                                        variant="outlined"
                                                    >
                                                        {localizedDisclaimer?.login_disclaimer?.cancel_button_text}
                                                    </Button>
                                                </div>
                                            )}
                                            <div key="login-button" className={style.loginButton}>
                                                <Button
                                                    data-tracking-action="Login"
                                                    size={buttonSize as ButtonSizes}
                                                    focusable
                                                    onClick={() => {
                                                        login();
                                                        setOpen(false);
                                                        onConfirm && onConfirm();
                                                    }}
                                                >
                                                    {localizedDisclaimer?.login_disclaimer?.login_button_text}
                                                </Button>
                                            </div>
                                            {!jnjFullBranded && (
                                                <div key="cancel-button" className={style.cancelButton}>
                                                    <Button
                                                        data-tracking-action="Cancel"
                                                        onClick={() => {
                                                            setOpen(false);
                                                            onClose && onClose();
                                                            onCancel && onCancel();
                                                        }}
                                                        variant="outlined"
                                                    >
                                                        {localizedDisclaimer?.login_disclaimer?.cancel_button_text}
                                                    </Button>
                                                </div>
                                            )}
                                        </Modal.Actions>
                                        <div key="footer-content" className={style.modalFooterContent}>
                                            <Modal.Content>
                                                {localizedDisclaimer?.login_disclaimer?.disclaimer_text && (
                                                    <div>
                                                        <Typography
                                                            color="gray"
                                                            size={width > JWMLoginDisclaimerBreakpoint ? "m" : "s"}
                                                        >
                                                            {localizedDisclaimer?.login_disclaimer?.disclaimer_text}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </Modal.Content>
                                        </div>
                                    </div>
                                </Modal>
                            </ModalImpression>
                        )}
                    </div>
                );
            }}
        </ResizeObserver>
    ) : null;
};
